export const AUTH_RETURN_DRAFT_STORAGE_KEY = "preventivonow-auth-return-draft-v2";
export const AUTH_RETURN_DRAFT_MAX_AGE_MS = 20 * 60 * 1_000;

const PROFESSIONS = ["Ingegnere", "Architetto", "Geometra", "Geologo"] as const;
const COMMISSIONERS = [
  "unknown",
  "private_individual",
  "private_enterprise",
  "bank",
  "insurance_company",
  "bank_or_insurance_controlled_or_mandated",
  "public_administration",
  "publicly_controlled_company",
  "securitisation_vehicle",
  "collection_agent",
] as const;
const CONTEXTS = [
  "unknown",
  "other_professional_engagement",
  "public_engineering_architecture_award",
] as const;
const PROCEDURES = [
  "other_or_unknown",
  "article_108_2_b_best_value",
  "article_50_1_b_direct_award",
] as const;
const INTERPRETATION_MODES = [
  "printed_source_strict",
  "professionearchitetto_v0_74",
] as const;

export interface SafeAuthReturnDraft {
  readonly profession: typeof PROFESSIONS[number];
  readonly screening: {
    readonly commissioner: typeof COMMISSIONERS[number];
    readonly context: typeof CONTEXTS[number];
    readonly procedure: typeof PROCEDURES[number];
    readonly employees: string;
    readonly revenue: string;
  };
  readonly calculation: {
    readonly categoryCode: string;
    readonly destinationCode: string;
    readonly serviceCode: string;
    readonly valueV: string;
    readonly inhabitants: string;
    readonly multiplier: string;
    readonly interpretationMode: typeof INTERPRETATION_MODES[number];
  };
  readonly senderProfile: {
    readonly studioName: string;
    readonly professionalName: string;
    readonly vatNumber: string;
    readonly address: string;
  };
  readonly scrollY: number;
}

interface StoredSafeAuthReturnDraft {
  readonly version: 2;
  readonly createdAt: number;
  readonly draft: SafeAuthReturnDraft;
}

export interface BrowserStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key))
    && allowed.every((key) => Object.hasOwn(value, key));
}

function isOneOf<T extends readonly string[]>(value: unknown, options: T): value is T[number] {
  return typeof value === "string" && options.includes(value);
}

function isShortCode(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9._-]{1,40}$/u.test(value);
}

function isNumericDraft(value: unknown): value is string {
  return typeof value === "string"
    && value.length <= 40
    && (value === "" || /^\d+(?:[.,]\d+)?$/u.test(value));
}

function isProfileDraftText(value: unknown, maximum: number): value is string {
  return typeof value === "string"
    && value.length <= maximum
    && !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u.test(value);
}

export function isSafeAuthReturnDraft(value: unknown): value is SafeAuthReturnDraft {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "profession",
    "screening",
    "calculation",
    "senderProfile",
    "scrollY",
  ])) return false;
  if (!isOneOf(value.profession, PROFESSIONS)) return false;
  if (!Number.isInteger(value.scrollY) || (value.scrollY as number) < 0 || (value.scrollY as number) > 1_000_000) {
    return false;
  }

  const screening = value.screening;
  if (!isRecord(screening) || !hasOnlyKeys(screening, [
    "commissioner",
    "context",
    "procedure",
    "employees",
    "revenue",
  ])) return false;
  if (
    !isOneOf(screening.commissioner, COMMISSIONERS)
    || !isOneOf(screening.context, CONTEXTS)
    || !isOneOf(screening.procedure, PROCEDURES)
    || !isNumericDraft(screening.employees)
    || !isNumericDraft(screening.revenue)
  ) return false;

  const calculation = value.calculation;
  if (!isRecord(calculation) || !hasOnlyKeys(calculation, [
    "categoryCode",
    "destinationCode",
    "serviceCode",
    "valueV",
    "inhabitants",
    "multiplier",
    "interpretationMode",
  ])) return false;
  if (!(isShortCode(calculation.categoryCode)
    && isShortCode(calculation.destinationCode)
    && isShortCode(calculation.serviceCode)
    && isNumericDraft(calculation.valueV)
    && isNumericDraft(calculation.inhabitants)
    && isNumericDraft(calculation.multiplier)
    && isOneOf(calculation.interpretationMode, INTERPRETATION_MODES))) return false;

  const senderProfile = value.senderProfile;
  return isRecord(senderProfile)
    && hasOnlyKeys(senderProfile, ["studioName", "professionalName", "vatNumber", "address"])
    && isProfileDraftText(senderProfile.studioName, 120)
    && isProfileDraftText(senderProfile.professionalName, 120)
    && isProfileDraftText(senderProfile.vatNumber, 32)
    && isProfileDraftText(senderProfile.address, 300);
}

export function saveSafeAuthReturnDraft(
  storage: BrowserStorageLike,
  draft: SafeAuthReturnDraft,
  now = Date.now(),
): void {
  if (!isSafeAuthReturnDraft(draft)) {
    throw new Error("La bozza di ritorno contiene campi non consentiti");
  }
  const stored: StoredSafeAuthReturnDraft = { version: 2, createdAt: now, draft };
  storage.setItem(AUTH_RETURN_DRAFT_STORAGE_KEY, JSON.stringify(stored));
}

export function consumeSafeAuthReturnDraft(
  storage: BrowserStorageLike,
  now = Date.now(),
): SafeAuthReturnDraft | null {
  const serialized = storage.getItem(AUTH_RETURN_DRAFT_STORAGE_KEY);
  storage.removeItem(AUTH_RETURN_DRAFT_STORAGE_KEY);
  if (serialized === null) return null;
  try {
    const stored: unknown = JSON.parse(serialized);
    if (!isRecord(stored) || !hasOnlyKeys(stored, ["version", "createdAt", "draft"])) {
      return null;
    }
    if (stored.version !== 2 || !Number.isInteger(stored.createdAt)) return null;
    const age = now - (stored.createdAt as number);
    if (age < 0 || age > AUTH_RETURN_DRAFT_MAX_AGE_MS) return null;
    return isSafeAuthReturnDraft(stored.draft) ? stored.draft : null;
  } catch {
    return null;
  }
}

export function saveSafeAuthReturnDraftForBrowser(
  sharedStorage: BrowserStorageLike,
  sameTabFallback: BrowserStorageLike,
  draft: SafeAuthReturnDraft,
  now = Date.now(),
): "shared" | "same_tab" | null {
  try {
    saveSafeAuthReturnDraft(sharedStorage, draft, now);
    try {
      sameTabFallback.removeItem(AUTH_RETURN_DRAFT_STORAGE_KEY);
    } catch {
      // A stale compatibility copy cannot block the shared-browser draft.
    }
    return "shared";
  } catch {
    try {
      saveSafeAuthReturnDraft(sameTabFallback, draft, now);
      return "same_tab";
    } catch {
      return null;
    }
  }
}

export function consumeSafeAuthReturnDraftFromBrowser(
  sharedStorage: BrowserStorageLike,
  sameTabFallback: BrowserStorageLike,
  now = Date.now(),
): SafeAuthReturnDraft | null {
  let restored: SafeAuthReturnDraft | null = null;
  for (const storage of [sharedStorage, sameTabFallback]) {
    try {
      const candidate = consumeSafeAuthReturnDraft(storage, now);
      if (restored === null && candidate !== null) restored = candidate;
    } catch {
      // One unavailable browser storage must not block the other one or the app.
    }
  }
  return restored;
}
